/**
 * The date-stamp vocabulary — which formats an originator can pick, and how wide
 * the resulting text is on the page.
 *
 * A date box is no longer a fixed rectangle: it IS its placeholder text, sized
 * to the format tokens at the chosen point size. That makes the box an honest
 * statement of what will appear in the signed document rather than a 150×40
 * rectangle that says nothing, which is the whole reason this module exists.
 */
import { PDFDocument, StandardFonts, type PDFFont } from 'pdf-lib';
import { useEffect, useState } from 'react';

/**
 * The formats offered as presets. Stored verbatim — the string an originator
 * picks here is the string BMO receives as `formatString`, so this list is the
 * wire vocabulary and not a display-only convenience.
 *
 * Anything outside this list is a custom format the originator typed. That is
 * how the settings modal knows to re-open on "Custom" without a second stored
 * flag: a value that is not a preset is, by definition, custom.
 */
export const DATE_FORMAT_PRESETS = [
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'YY/MM/DD',
  'DD/MM/YYYY',
  'DD/MM/YY',
  'MM/DD/YYYY',
  'MM/DD/YY',
] as const;

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

/** Points. Close to the body size of the BMO forms these dates sit inside. */
export const DEFAULT_DATE_FONT_SIZE = 10;

/**
 * Point-size bounds, mirrored in `WorkflowStep.MinDateFontSize` /
 * `MaxDateFontSize`. Below the minimum the date is unreadable in print; above
 * the maximum it cannot plausibly fit a date area on a form.
 */
export const MIN_DATE_FONT_SIZE = 6;
export const MAX_DATE_FONT_SIZE = 72;

/** Whether a stored format is one of the presets, or something typed. */
export function isPresetDateFormat(format: string): boolean {
  return (DATE_FORMAT_PRESETS as readonly string[]).includes(format);
}

/** The extent of a run of text, in PDF points. */
export type TextExtent = { width: number; height: number };

/** Measures a string at a point size, in PDF points. */
export type MeasureDateText = (text: string, fontSize: number) => TextExtent;

/**
 * Helvetica's metrics, loaded once per session.
 *
 * Deliberately pdf-lib rather than a canvas `measureText`: the box authored here
 * has to agree with the document `fillPdf.ts` writes, and `fillPdf` draws in
 * pdf-lib's Helvetica. Measuring the browser's Helvetica — which on Windows is
 * really Arial, and on Linux is whatever fontconfig decides — would make the two
 * agree only by luck. Helvetica is one of the 14 standard fonts, so this embeds
 * no font program and the throwaway document costs nothing.
 */
let fontPromise: Promise<PDFFont> | null = null;

function helvetica(): Promise<PDFFont> {
  fontPromise ??= PDFDocument.create().then((doc) => doc.embedFont(StandardFonts.Helvetica));
  return fontPromise;
}

export async function loadDateTextMeasure(): Promise<MeasureDateText> {
  const font = await helvetica();
  return (text, fontSize) => ({
    width: font.widthOfTextAtSize(text, fontSize),
    height: font.heightAtSize(fontSize),
  });
}

/**
 * The measurer, or null until the font resolves.
 *
 * Null rather than a placeholder estimate: a wrong width would be written back
 * to the config on the next drag and silently become the authored footprint.
 * Callers fall back to the stored dimensions for the frame or two this takes,
 * which is the last correct value anyone chose.
 */
export function useDateTextMeasure(): MeasureDateText | null {
  const [measure, setMeasure] = useState<MeasureDateText | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadDateTextMeasure().then((fn) => {
      // Stored via the updater form: a function in state would otherwise be
      // called by React as a lazy initializer instead of being stored.
      if (!cancelled) setMeasure(() => fn);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return measure;
}
